import React, { useMemo, useState, useRef } from "react";
import "./ResultsPage.css";
import SignupForm from "./SignupForm";

/**
 * The keys (id) MUST match exactly what the backend returns in result.axes.
 */

/* ---------- Axis metadata ---------- */
const AXIS_DEFS = [
  { id: "Analytical Depth", group: "Intellectual Acuity & Cognitive Style",
    high: "You tend to break things down into small, clear pieces and enjoy understanding every detail.",
    low:  "You quickly see the overall picture and focus on the main themes rather than the small parts." },
  { id: "Cognitive Flexibility & Abstraction", group: "Intellectual Acuity & Cognitive Style",
    high: "You are comfortable changing your viewpoint and mixing ideas to come up with creative solutions.",
    low:  "You prefer a straightforward, step-by-step way of thinking, sticking to familiar methods." },
  { id: "Emotional Regulation", group: "Emotional Regulation & Resilience",
    high: "You keep your emotions steady and respond in a calm, measured way.",
    low:  "You feel your emotions strongly and react quickly, showing genuine, immediate responses." },
  { id: "Stress Resilience & Outlook", group: "Emotional Regulation & Resilience",
    high: "You stay calm under pressure and look forward with a flexible mindset.",
    low:  "You focus on the here-and-now, dealing with stress by addressing current challenges as they come." },
  { id: "Social Engagement", group: "Interpersonal Engagement & Social Influence",
    high: "You enjoy meeting lots of people and feel energized in group settings.",
    low:  "You prefer meaningful, one-on-one conversations and quieter interactions." },
  { id: "Persuasive & Assertive Communication", group: "Interpersonal Engagement & Social Influence",
    high: "You share your ideas directly and confidently, making it easy for others to understand you.",
    low:  "You choose your words carefully, expressing your thoughts in a thoughtful and gentle way." },
  { id: "Intrinsic Motivation & Autonomy", group: "Motivational & Value Orientation",
    high: "You are self-motivated and enjoy setting and pursuing your own goals.",
    low:  "You draw energy from working with others and value the input you get from your community." },
  { id: "Ethical, Cultural & Moral Values", group: "Motivational & Value Orientation",
    high: "You stick to clear principles that guide your choices and actions.",
    low:  "You are flexible with your values, adapting them based on different situations and practical needs." },
  { id: "Leadership Style", group: "Leadership, Influence & Conflict Management",
    high: "You like to inspire and bring people together with a shared vision.",
    low:  "You focus on clear, direct actions and making decisions in a structured way." },
  { id: "Conflict Management", group: "Leadership, Influence & Conflict Management",
    high: "You try to solve problems together and seek common ground during disagreements.",
    low:  "You tackle conflicts head-on with clear actions and firm boundaries." },
  { id: "Risk & Impulse Control", group: "Decision-Making & Behavioral Control",
    high: "You carefully consider risks and balance bold actions with thoughtful planning.",
    low:  "You tend to act quickly, grabbing opportunities as soon as they appear." },
  { id: "Decision Efficiency & Temporal Orientation", group: "Decision-Making & Behavioral Control",
    high: "You make fast decisions that keep long-term goals in mind.",
    low:  "You take more time to focus on the present details, ensuring your choices fit current needs." },
  { id: "Self-Organization & Learning Style", group: "Self-Organization & Adaptive Disposition",
    high: "You work best with clear routines and organized methods for handling tasks and learning.",
    low:  "You prefer a spontaneous, flexible approach that adapts easily to change." },
  { id: "Adaptive Disposition & Self-Concept", group: "Self-Organization & Adaptive Disposition",
    high: "You see yourself as open to change and comfortable adjusting to new situations.",
    low:  "You maintain a steady sense of who you are, valuing consistency and a clear self-image." },
];

/* ---------- Distribution placeholder (for subtle background wave) ---------- */
function placeholderDistribution(seed = 0.37) {
  const points = [];
  const mean = 50, sigma = 18, skew = seed * 0.6 - 0.3;
  for (let x = 0; x <= 100; x++) {
    const z = (x - mean) / sigma;
    let y = Math.exp(-0.5 * z * z);
    y *= 1 + skew * (x - mean) / 50;
    points.push([x, y]);
  }
  const maxY = Math.max(...points.map(p => p[1])) || 1;
  return points.map(([x, y]) => [x, y / maxY]);
}

/* ---------- Axis Bar ---------- */
function AxisBar({ title, value, highText, lowText }) {
  const [open, setOpen] = useState(false);
  const dist = useMemo(() => placeholderDistribution(), []);
  const pos = (value + 100) / 2; // 0..100
  const fillLeft = Math.max(0, 50 - pos);
  const fillRight = Math.max(0, pos - 50);

  return (
    <div className="axis">
      <div className="axis-head">
        <div className="axis-title">{title}</div>
        <button className="axis-info" type="button" aria-label="More info" onClick={() => setOpen(s => !s)}>i</button>
      </div>

      <div
        className="axis-bar"
        role="progressbar"
        aria-label={`${title} score`}
        aria-valuemin={-100}
        aria-valuemax={100}
        aria-valuenow={value}
      >
        <svg className="axis-dist" viewBox="0 0 100 12" preserveAspectRatio="none">
          <path d={"M 0 12 " + dist.map(([x, y]) => `L ${x} ${12 - y * 10}`).join(" ") + " L 100 12 Z"} />
        </svg>
        <div className="axis-fill axis-fill-left" style={{ width: `${fillLeft}%` }} />
        <div className="axis-fill axis-fill-right" style={{ width: `${fillRight}%` }} />
        <div className="axis-center" />
        <div className="axis-marker" style={{ left: `${pos}%` }} />
      </div>

      {open && (
        <div className="axis-blurbs" role="region" aria-live="polite">
          <div><span className="axis-side">Left:</span> {lowText}</div>
          <div><span className="axis-side">Right:</span> {highText}</div>
        </div>
      )}
    </div>
  );
}

/* ---------- Quips ---------- */
const QUIPS = [
  "This gives… main character energy.",
  "And you thought you were Hufflepuff…",
  "Certified vibe architect.",
  "Your friends are now re-evaluating everything...",
  "Big “I have a system for that” energy.",
  "The spreadsheet respects you.",
  "Therapist would be proud. Probably.",
  "Chaotic good? Lawful chill? You decide.",
  "You contain multitudes. Some of them color-coded.",
];

export default function ResultsPage({ result, onSignupSuccess }) {
  const [quip] = useState(() => QUIPS[Math.floor(Math.random() * QUIPS.length)]);
  const cardRef = useRef(null);

  // prefer prop, else localStorage (vybly_quiz_results.result.axes)
  const axesObj = useMemo(() => {
    const fromProp = result?.axes;
    if (fromProp && typeof fromProp === "object") return fromProp;
    try {
      const stored = JSON.parse(localStorage.getItem("vybly_quiz_results") || "{}");
      const root = stored?.result ?? stored;
      if (root?.axes && typeof root.axes === "object") return root.axes;
    } catch {}
    return null;
  }, [result]);

  const axesPercent = useMemo(() => {
  const fromProp = result?.axes_percent;
  if (fromProp && typeof fromProp === "object") return fromProp;
  try {
    const stored = JSON.parse(localStorage.getItem("vybly_quiz_results") || "{}");
    const root = stored?.result ?? stored;
    if (root?.axes_percent && typeof root.axes_percent === "object") return root.axes_percent;
  } catch {}
  return null;
}, [result]);

  const mapped = useMemo(() => {
  if (!axesObj && !axesPercent) return null;  // allow either source

  return AXIS_DEFS.map(def => {
    const raw = axesObj?.[def.id] ?? 0;
    const pct = axesPercent?.[def.id] ?? Math.round(raw * 100); // fallback while migrating
    return {
      id: def.id,
      title: def.id,
      highText: def.high,
      lowText: def.low,
      value: pct,
      raw,
    };
  });
}, [axesObj, axesPercent]);

  const matchesCount = useMemo(() => 200 + Math.floor(Math.random() * 101), []);

  /* ---------- Share as PNG (with native share on mobile) ---------- */
  // Helpers (put these above handleShare inside ResultsPage.jsx file)
  function base64urlEncode(str) {
    return btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/,"");
  }

  function buildShareUrl(axesPercent, quip) {
    // minimal payload; small enough to fit in a URL comfortably
    const payload = { axes_percent: axesPercent, q: quip };
    const enc = base64urlEncode(JSON.stringify(payload));
    const u = new URL("/share", window.location.origin);
    u.searchParams.set("r", enc);
    return u.href;
  }

  // ---- Production-ready hybrid share ----
  // ---- Production-grade share: mobile = native share; desktop = copy link + download PNG
  async function handleShare() {
    try {
      const node = cardRef.current;
      if (!node) return;

      // Wait for fonts so html2canvas renders crisp text
      if (document.fonts?.ready) { try { await document.fonts.ready; } catch {} }

      // Lazy-load to keep the main bundle small
      const { default: html2canvas } = await import("html2canvas");

      // Cap DPR so we don't explode memory on high-DPI devices
      const dpr = Math.min(2, window.devicePixelRatio || 1);

      // Render the results card to a canvas
      const canvas = await html2canvas(node, {
        backgroundColor: "#0f1922",  // match card bg so PNG isn't transparent
        scale: dpr,
        useCORS: true,
        logging: false,
        removeContainer: true,
        imageTimeout: 15000,
      });

      // Create a PNG blob (Safari-safe)
      let blob = await new Promise(res => canvas.toBlob?.(res, "image/png", 0.95));
      if (!blob) {
        const dataURL = canvas.toDataURL("image/png", 0.95);
        blob = await (await fetch(dataURL)).blob();
      }
      const file = new File([blob], "vybly-vibe-map.png", { type: "image/png" });

      const title = "My VyBly Vibe Map";
      const text  = (typeof quip === "string" && quip) || "Here’s my VyBly vibe map.";
      const url   = buildShareUrl(axesPercent, text);   // stay client-only

      // Treat “mobile” as the only place we ever show the OS share sheet
      const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

      // 1) Best UX on mobile: native share with an actual image file
      if (isMobile && navigator.share && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title, text });
          return; // done
        } catch (e) {
          // user canceled -> just stop quietly
          if (/AbortError|cancel/i.test(String(e))) return;
        }
      }

      // 2) Still on mobile but no file share: share a link
      if (isMobile && navigator.share) {
        try {
          await navigator.share({ title, text, url });
          return;
        } catch (e) {
          if (/AbortError|cancel/i.test(String(e))) return;
        }
      }

      // 3) Desktop: copy link (robust) + download PNG automatically
      // Try modern async clipboard API first
      let copied = false;
      try {
        await navigator.clipboard?.writeText?.(url);
        copied = true;
      } catch {
        // Fallback for older/locked-down desktops
        try {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          ta.remove();
          copied = true;
        } catch {}
      }

      // Kick off a download so they instantly have the image to drop anywhere
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = "vybly-vibe-map.png";
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(dlUrl); } catch {} }, 0);

      // Small, clear confirmation (swap for your toast if you have one)
      if (copied) {
        alert("Share link copied. Your image just downloaded—share it anywhere!");
      } else {
        alert("Your image just downloaded. If you also want a link, use the copy button next to Share.");
      }
    } catch (err) {
      console.error("Share failed:", err);
      try { alert("Sorry—sharing failed on this device. The image download should still work."); } catch {}
    }
  }

  return (
    <div className="results-shell">
      <div className="results-left">
        <div className="results-header">
          <h1 className="results-h1">Your vibe map is ready.</h1>
          {/* <p className="results-sub">Zero-center bars. No judgments—just how you roll.</p> */}
          <div className="results-quip">{quip}</div>
          <button className="share-btn" type="button" onClick={handleShare}>
            Share image
          </button>
        </div>

        <div className="results-card" ref={cardRef}>
          {!mapped ? (
            <div className="results-empty">No results to show.</div>
          ) : (
            <>
              {mapped.map(a => (
                <AxisBar
                  key={a.id}
                  title={a.title}
                  value={a.value}
                  highText={a.highText}
                  lowText={a.lowText}
                />
              ))}

              <div className="matches-note">
                We found <strong>{matchesCount}</strong> people you’ll likely vybe with.
              </div>
            </>
          )}
        </div>
      </div>

      <aside className="results-right">
        <div className="signup-card">
          <h3>Save your results &<br/>join us</h3>
          <SignupForm
            onSignupSuccess={onSignupSuccess}
            autocompleteUsername="username"
            autocompleteEmail="email"
            autocompletePassword="current-password"
            autocompletePhone="tel"
          />
          <p className="privacy-note">
            Your email and phone help secure your account and preserve your anonymity.
          </p>
        </div>
      </aside>
    </div>
  );
}
